/* Copyright Flexday Solutions LLC, Inc - All Rights Reserved

 * Unauthorized copying of this file, via any medium is strictly prohibited

 * Proprietary and confidential

 * See file LICENSE.txt for full license details.

 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CustomToolbar from '../components/customToolbar';
import Header from '../../../components/form/header.component';
import PageContainer from '../../../components/pageContainer';
import { SECTIONS } from '../../../constants/drawer';
import { ACCESS_CONTROL } from '../../../constants/path';
import { ROLES, ADD_PAGE } from '../../../constants/accessControlPaths';
import { NoDataTitle, TableStyled } from '../styled';
import { RBAC_ROLES_ENDPOINT } from '../../../constants/accessControl';
import {
  useDeleteManyRBACMutation,
  useLazyGetAllRBACQuery,
} from '../../../redux/services/speciphicAsk';
import Loader from '../../../components/loader/loader.component';
import DisplayTime from '../../../components/timezone.component';
import { filterByIds, sortByDateField } from '../../../utils/functions';
import { pushToast } from '../../../redux/reducers/toasts.slice';
import UnauthorizedMessage from '../../../components/unauthorized';
import DialogComponent from '../../../components/app.dialog';

const addPageRoute = `${ACCESS_CONTROL}${ROLES.endpoint}${ADD_PAGE.endpoint}`;

const translationJSONPrefix = `accessControlSection.roles.mainPage`;

const unauthorizedTranslationPrefix = `${translationJSONPrefix}.unauthorizedMessages`;

const columns = (t) => {
  const translationPrefix = `${translationJSONPrefix}.table.columnHeaders`;

  return [
    {
      field: 'name',
      headerName: t(`${translationPrefix}.roleName`),
      flex: 0.5,
      minWidth: 150,
      valueGetter: (params) => `${params.row?.name}`,
    },
    {
      field: 'policies',
      headerName: t(`${translationPrefix}.policies`),
      flex: 0.2,
      minWidth: 80,
      valueGetter: (params) => params.row?.policyCount,
    },
    {
      field: 'description',
      headerName: t(`${translationPrefix}.description`),
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'createdBy',
      headerName: t(`${translationPrefix}.createdBy`),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'createdAt',
      headerName: t(`${translationPrefix}.createdAt`),
      flex: 0.5,
      minWidth: 200,
      renderCell: (params) => <DisplayTime time={params.row?.createdDate} />,
    },
    {
      field: 'updatedAt',
      headerName: t(`${translationPrefix}.updatedAt`),
      flex: 0.5,
      minWidth: 200,
      renderCell: (params) => (
        <DisplayTime time={params.row?.lastUpdatedDate} />
      ),
    },
  ];
};

const AccessControlRolesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [selectedRoles, setSelectedRoles] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogTFValue, setDeleteDialogTFValue] = useState('');
  const [dialogDeleteButtonDisabled, setDialogDeleteButtonDisabled] =
    useState(true);

  const [getAllRBAC, { data, error, isError, isLoading, isSuccess }] =
    useLazyGetAllRBACQuery();

  const [
    deleteRBACs,
    {
      data: deletedResponse,
      error: deleteError,
      isError: isDeleteError,
      isLoading: isDeleteLoading,
      isSuccess: isDeleteSuccess,
    },
  ] = useDeleteManyRBACMutation();

  useEffect(() => {
    setDialogDeleteButtonDisabled(true);
    setDeleteDialogTFValue('');
  }, [deleteDialogOpen]);

  useEffect(() => {
    if (deleteError?.status === 403) {
      dispatch(
        pushToast({
          message: t(`${unauthorizedTranslationPrefix}.delete`),
          severity: 'error',
        }),
      );
    }
  }, [isDeleteError]);

  useEffect(() => {
    getAllRBAC({
      endpoint: RBAC_ROLES_ENDPOINT,
    });

    setSelectedRoles([]);
  }, [deletedResponse]);

  useEffect(() => {
    setDeleteDialogOpen(false);
  }, [isDeleteSuccess]);

  const handleCellClick = (e) => {
    if (e?.field === 'name') navigate(`${addPageRoute}?id=${e?.id}`);
  };

  if (error?.status === 403)
    return (
      <UnauthorizedMessage
        drawerOpen={isDrawerOpen}
        drawer={SECTIONS.ACCESS_CONTROL}
        description={t(`${unauthorizedTranslationPrefix}.list`)}
      />
    );

  return (
    <PageContainer
      isDrawerOpen={(res) => setIsDrawerOpen(res)}
      drawer={SECTIONS.ACCESS_CONTROL}
    >
      <DialogComponent
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        setTextFieldValue={setDeleteDialogTFValue}
        textField={{
          name: 'name',
          value: deleteDialogTFValue,
          onChange: (e) => {
            setDeleteDialogTFValue(e.target.value);

            if (selectedRoles.length > 1) {
              if (e.target.value === 'permanently delete')
                setDialogDeleteButtonDisabled(false);
              return;
            }

            const p_names = filterByIds(selectedRoles, data).map((p) => p.name);

            setDialogDeleteButtonDisabled(!(e.target.value === p_names[0]));
          },
          placeholder:
            selectedRoles.length > 1
              ? t(
                  `accessControlSection.roles.deleteDialog.textFieldPlaceholderPlural`,
                )
              : t(
                  `accessControlSection.roles.deleteDialog.textFieldPlaceholder`,
                ),
        }}
        title={`${t(`accessControlSection.roles.deleteDialog.title`)}`}
        content={
          selectedRoles.length > 1
            ? t(`accessControlSection.roles.deleteDialog.contentPlural`)
            : `${t(`accessControlSection.roles.deleteDialog.content`)}`
        }
        highlightedTitle={
          selectedRoles.length === 1
            ? `${t(`accessControlSection.roles.deleteDialog.highlightedTitle`)}`
            : ''
        }
        highlightedContent={`${
          selectedRoles.length === 1
            ? filterByIds(selectedRoles, data)[0]?.name
            : ''
        }`}
        onSubmit={(e) => {
          deleteRBACs({
            endpoint: RBAC_ROLES_ENDPOINT,
            ids: selectedRoles,
          });
        }}
        submitBtnDisabled={dialogDeleteButtonDisabled}
        isDeleteLoading={isDeleteLoading}
        cancelBtnLabel={t(
          `accessControlSection.roles.deleteDialog.buttons.cancel`,
        )}
        submitBtnLabel={t(
          `accessControlSection.roles.deleteDialog.buttons.delete`,
        )}
      />
      <Header
        header={t(`${translationJSONPrefix}.title`)}
        addButtonLabel={t(`${translationJSONPrefix}.addButtonLabel`)}
        deleteButton={true}
        deleteButtonDisabled={selectedRoles.length === 0}
        addButtonOnClick={() => navigate(addPageRoute)}
        deleteButtonOnClick={() => setDeleteDialogOpen(true)}
        isDeleteLoading={isDeleteLoading}
      />
      {isLoading ? (
        <Loader label={t(`${translationJSONPrefix}.loadingLabel`)} />
      ) : data?.length > 0 ? (
        <TableStyled
          onCellClick={handleCellClick}
          rows={sortByDateField([...data], 'lastUpdatedDate')}
          columns={columns(t)}
          pageSize={10}
          checkboxSelection
          autoHeight
          onSelectionModelChange={setSelectedRoles}
          components={{
            Toolbar: CustomToolbar,
          }}
        />
      ) : (
        <NoDataTitle>{t(`${translationJSONPrefix}.noDataLabel`)}</NoDataTitle>
      )}
    </PageContainer>
  );
};

export default AccessControlRolesPage;
